import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { LearnerCalendarResponse, LearnerSession, getLearnerCalendar } from '../../api/learner';
import { useLearnerSpace } from '../../learner/LearnerSpace';
import {
  formatDayAndTime,
  joinStateAt,
  useServerClock,
  waitInWords,
  zoneLabel,
} from '../../learner/learnerTime';
import SessionRoom from '../SessionRoom';
import SafetyReportAction from '../../components/SafetyReportAction';

const DAY = 86_400_000;
/**
 * How often a waiting screen re-reads the schedule, so a class cancelled or
 * moved while the learner waits sends them to its new state (A3). A refresh
 * cadence, not a policy value: nothing about entry depends on it.
 */
const RECHECK_MS = 60_000;

type Load =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'missing' }
  | { status: 'ready'; data: LearnerCalendarResponse; session: LearnerSession };

/** The governed rule beside its value (S08-PAR-01), small, for the adult who asks. */
const policyNote = (data: LearnerCalendarResponse) =>
  `Regla session_prejoin_minutes = ${data.joinPolicy.prejoinMinutes} min, session_grace_minutes = ${
    data.joinPolicy.graceMinutes
  } min · ${data.joinPolicy.policyVersion || 'valores por defecto'}`;

/**
 * UC-S08 — the door to a live class (STU-10-FR-01..08).
 *
 * The session is found in the learner's own calendar, which the server
 * scoped; a booking that is not there — a sibling's, a cancelled one, a stale
 * link — is "not found" and nothing else (S08-GT-03, GT-04). Before the
 * governed window opens there is a countdown and nothing is recorded
 * (S08-GT-02). Inside it, the recording notice and the camera/microphone
 * guidance come first; the learner's tap acknowledges the notice and is not a
 * consent record (S08-SAF-05). The room itself asks the server for fresh join
 * info on every entry — nothing durable is held or shown (FR-08), and the
 * server checks the window and the learner again at that moment.
 */
const JoinClass: React.FC = () => {
  const { bookingId = '' } = useParams<{ bookingId: string }>();
  const { studentId } = useLearnerSpace();
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [entered, setEntered] = useState(false);
  const [withCamera, setWithCamera] = useState(true);
  const [attempt, setAttempt] = useState(0);

  const fetchSession = useCallback(
    (quiet = false) => {
      if (!studentId) return;
      if (!quiet) setLoad({ status: 'loading' });
      const from = new Date(Date.now() - DAY).toISOString();
      const to = new Date(Date.now() + 60 * DAY).toISOString();
      getLearnerCalendar(studentId, from, to)
        .then((data) => {
          const session = data.calendar.find((s) => s.bookingId === bookingId);
          setLoad(session ? { status: 'ready', data, session } : { status: 'missing' });
        })
        .catch(() => setLoad({ status: 'error' }));
    },
    [bookingId, studentId],
  );

  useEffect(() => fetchSession(), [fetchSession]);

  const now = useServerClock(load.status === 'ready' ? load.data.serverNow : undefined);
  const state = load.status === 'ready' ? joinStateAt(load.session.joinWindow, now) : undefined;

  useEffect(() => {
    if (entered || state !== 'BEFORE_WINDOW') return;
    const id = setInterval(() => fetchSession(true), RECHECK_MS);
    return () => clearInterval(id);
  }, [entered, state, fetchSession]);

  if (load.status === 'loading') {
    return (
      <p className="hi-s" role="status">
        Buscando tu clase…
      </p>
    );
  }

  if (load.status === 'error') {
    return (
      <div className="lrn-status" role="alert">
        <h2>No pudimos abrir la puerta de la clase</h2>
        <p>No es culpa tuya. Prueba otra vez en un momento.</p>
        <button className="ag-b" onClick={() => fetchSession()}>
          Intentar otra vez
        </button>
      </div>
    );
  }

  if (load.status === 'missing') {
    return (
      <div className="lrn-status" role="alert">
        <h2>No encontramos esta clase</h2>
        <p>Puede que haya cambiado de día. Mira tu calendario.</p>
        <Link className="ag-b" to="/student/calendar">
          Ver mi calendario
        </Link>
      </div>
    );
  }

  const { data, session } = load;
  const title = session.courseTitle ?? 'Tu clase';
  // UC-S09 — the child can say something was not OK from the class itself,
  // about this class. Draws nothing unless the server has the intake on.
  const report = studentId ? (
    <div className="lrn-report">
      <SafetyReportAction
        studentId={studentId}
        enrollmentId={session.enrollmentId}
        seq={session.seq}
      />
    </div>
  ) : null;

  if (entered) {
    return (
      <div className="lrn-room">
        <SessionRoom
          key={attempt}
          backTo="/student"
          onRejoin={() => setAttempt((n) => n + 1)}
          startWithCamera={withCamera}
        />
        {report}
      </div>
    );
  }

  if (state === 'ENDED') {
    return (
      <div className="lrn-status">
        <h2>{title}</h2>
        <p>Esta clase ya terminó.</p>
        <Link className="ag-b" to="/student/past">
          Ver mis clases pasadas
        </Link>
        {report}
      </div>
    );
  }

  if (state === 'BEFORE_WINDOW') {
    const wait = new Date(session.joinWindow.opensAt).getTime() - now.getTime();
    return (
      <div className="lrn-status">
        <span className="lrn-tag wait">Todavía no es la hora</span>
        <h2>{title}</h2>
        <p>Empieza el {formatDayAndTime(session.scheduledStart, data.timeZone)}.</p>
        <p role="timer" aria-live="off">
          Faltan {waitInWords(wait)} para poder entrar.
        </p>
        <p>
          La puerta se abre {data.joinPolicy.prejoinMinutes} minutos antes de empezar. No pasa nada:
          esperar no cuenta como falta.
        </p>
        <p className="lrn-zone">{zoneLabel(data.timeZone)}</p>
        <p className="lrn-policy">{policyNote(data)}</p>
        {report}
      </div>
    );
  }

  return (
    <div className="lrn-status">
      <span className="lrn-tag">
        {state === 'LATE' ? 'La clase ya empezó' : 'Ya puedes entrar'}
      </span>
      <h2>{title}</h2>
      {state === 'LATE' && <p>Llegas un poco tarde. Puedes entrar igual.</p>}

      <section aria-labelledby="rec-notice">
        <h3 className="h2" id="rec-notice">
          Esta clase se graba
        </h3>
        <p>Te lo decimos para que lo sepas. Tu familia dio el permiso al inscribirte.</p>
      </section>

      <section aria-labelledby="media-help">
        <h3 className="h2" id="media-help">
          Cámara y micrófono
        </h3>
        <p>
          Tu navegador te va a preguntar si puede usar la cámara y el micrófono. Pulsa «Permitir».
          Si sale mal, no pasa nada: podrás intentarlo otra vez.
        </p>
        <p>
          No necesitas la cámara para que cuente que estuviste en clase. Si no quieres o no puedes
          encenderla, entra igual: también puedes entrar sin micrófono y escuchar.
        </p>
      </section>

      <button
        className="ag-b"
        onClick={() => {
          setWithCamera(true);
          setEntered(true);
        }}
      >
        Entendido, entrar a la clase
      </button>
      <button
        className="ag-b"
        onClick={() => {
          setWithCamera(false);
          setEntered(true);
        }}
      >
        Entrar con la cámara apagada
      </button>
      <p className="lrn-policy">{policyNote(data)}</p>
      {report}
    </div>
  );
};

export default JoinClass;
